import { useMemo, useRef, useState } from 'react';
import { Modal, Input } from 'antd';
import {
  MenuOutlined, EyeOutlined, EyeInvisibleOutlined, SearchOutlined,
} from '@ant-design/icons';
import './styles.scss';

export interface ColumnConfig {
  key:       string;
  label:     string;
  visible:   boolean;
  disabled?: boolean;   // UI-only columns that can't be toggled/reordered
}

interface Props {
  open:     boolean;
  onClose:  () => void;
  columns:  ColumnConfig[];
  onChange: (cols: ColumnConfig[]) => void;
}

const CustomiseColumnsModal = ({ open, onClose, columns, onChange }: Props) => {
  const [search, setSearch]     = useState('');
  const dragIndex               = useRef<number | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return columns;
    const q = search.toLowerCase();
    return columns.filter(c => c.label.toLowerCase().includes(q));
  }, [columns, search]);

  const toggle = (key: string) => {
    onChange(columns.map(c => (c.key === key && !c.disabled ? { ...c, visible: !c.visible } : c)));
  };

  // Native HTML5 drag reorder (disabled while searching / for disabled rows).
  const canReorder = !search.trim();

  const handleDrop = (targetKey: string) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from == null) return;
    const fromKey = columns[from]?.key;
    if (!fromKey || fromKey === targetKey) return;
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    const to = next.findIndex(c => c.key === targetKey);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      centered
      destroyOnHidden
      className="cc-modal"
      title={null}
    >
      <div className="cc-body">
        <h2 className="cc-title">Customise Columns</h2>
        <p className="cc-sub">
          You can show, hide, and reorder columns on your client list to display the
          fields you care about the most.
        </p>

        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined />}
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="cc-search"
        />

        <div className="cc-list">
          {filtered.map((col) => {
            const realIndex = columns.findIndex(c => c.key === col.key);
            return (
              <div
                key={col.key}
                className={`cc-row ${col.disabled ? 'disabled' : ''} ${!col.visible ? 'hidden-col' : ''}`}
                draggable={canReorder && !col.disabled}
                onDragStart={() => { if (canReorder && !col.disabled) dragIndex.current = realIndex; }}
                onDragOver={e => { if (canReorder) e.preventDefault(); }}
                onDrop={() => { if (canReorder && !col.disabled) handleDrop(col.key); }}
              >
                <span className="cc-handle"><MenuOutlined /></span>
                <span className="cc-label">{col.label}</span>
                <button
                  type="button"
                  className="cc-eye"
                  disabled={col.disabled}
                  onClick={() => toggle(col.key)}
                  title={col.visible ? 'Hide column' : 'Show column'}
                >
                  {col.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="cc-empty">No columns found</div>}
        </div>
      </div>
    </Modal>
  );
};

export default CustomiseColumnsModal;
